import React, { useState, useMemo, useContext } from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import DataTable from "react-data-table-component";
import { NotificationManager } from "react-notifications";
import { makeStyles } from "@mui/styles";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckIcon from '@mui/icons-material/Check';
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";
import { deleteCEECampaignMembershipCase, downloadFile } from "services/ConfigServices";
import CaseDialog from "./CaseDialog";
import EditCaseStatusDialog from "./EditCaseStatusDialog";
import CustomDeleteButton from "components/CustomDeleteButton/CustomDeleteButton.js";
import UserService from "services/UserService";
import Button from "components/CustomButtons/Button.js";
import styled from "styled-components";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import { ceecase } from "variables/cee";
import { ceecaseStatusData } from "variables/lists/cee";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import { withStyles } from "@mui/styles";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import CustomDialogTitle from "components/Dialog/CustomDialogTitle.js";
import CEECampaignsContext from "context/CEECampaignsContext";
import useCasesTable from "./useCasesTable.js";


const GroupCell = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Container = styled.div`
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);
  border-top: 4px solid rgba(255, 255, 255, 1);
  background-color: white;
  margin-top: -0.5px;
`;

const ExpandableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 4rem 2rem;
`;
const ExpandableElement = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: ${(props) => props.marginleft};
`;

const ElementTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementData = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementSectionTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  font-family: "Source Sans Pro",sans-serif;
  text-decoration: underline;
  margin-top: 10px;
`;

const NoData = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  opacity: 1;
`;

const ElementDataDownload = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
  cursor: pointer;
  text-decoration: underline;
`;


const DialogContent = withStyles((theme) => ({
  root: {
    padding: '16px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: '8px',
  },
}))(MuiDialogActions);


const downloadGED = (fileId, fileName) => async () => {
  axios
    .get(
      downloadFile(fileId),
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      }
    )
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); //or any other extension
      document.body.appendChild(link);
      link.click();
    });
};


const useStyles = makeStyles(styles);

const RTCases = () => {
  const permis = JSON.parse(localStorage.getItem("permission")) || [];
  const cee_update = permis.includes("cee_update");
  const cee_admin = permis.includes("cee_admin");
  const { gridState, refetch, setSelectedCases } = useCasesTable();
  const ceeCampaignContext = useContext(CEECampaignsContext);
  const { ceecampaign, setCeecampaign } = ceeCampaignContext;
  const advancedSearchContext = useContext(AdvancedSearchContext);
  const { groupInfoListById, setPage } = advancedSearchContext;
  const [open, setOpen] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [openValidateOrder, setOpenValidateOrder] = useState(false);
  const [currentCase, setCurrentCase] = useState(null);

  let dataCases = gridState.data;

  const classes = useStyles();

  const columns = [
    {
      name: ceecase.name,
      selector: (row) => row.parameters.name,
      sortable: true,
      maxWidth: "13rem",
    },
    {
      name: ceecase.ceeform,
      selector: (row) => row.parameters.ceeform,
      sortable: true,
      maxWidth: "13rem",
    },
    {
      name: ceecase.kwhcumac,
      selector: (row) => row.parameters.kwhcumac,
      sortable: true,
      maxWidth: "7rem",
    },
    {
      name: ceecase.referentname,
      selector: (row) => row.parameters.referentname,
      sortable: true,
      maxWidth: "13rem",
      cell: (row) =>
        <div>
          {row.parameters.referentname}
        </div>
    },
    {
      name: ceecase.ceecampaignmembershipname,
      selector: (row) => row.ceecampaignmembershipname,
      sortable: true,
      maxWidth: "9rem",
    },
    {
      name: ceecase.creationdate,
      selector: (row) => row.parameters.creationdate,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.creationdate && moment(row.parameters.creationdate).isValid()
          ? moment(row.parameters.creationdate).format("DD/MM/YYYY HH:mm")
          : "",
    },
    {
      name: ceecase.status,
      selector: (row) => row.parameters.status,
      sortable: true,
      maxWidth: "12rem",
      cell: (row) => row.parameters.status ?
        <div style={{ color: ceecaseStatusData.find((x) => x.id == row.parameters.status).color, fontWeight: "bold" }}>
          {ceecaseStatusData.find((x) => x.id == row.parameters.status).name}
        </div> : <></>
    },
    {
      name: "",
      sortable: false,
      width: "10rem",
      // eslint-disable-next-line react/button-has-type
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <>
          <div className="actions-right">
            {
              cee_admin && row.parameters.status !== 'membervalidation' &&
              <Tooltip title="Changer le statut">
                <Button
                  onClick={editCaseStatus(row)}
                  color={"info"}
                  className={classes.actionButton}
                >
                  <ExitToAppIcon fontSize="large" />
                </Button>
              </Tooltip>
            }
            {cee_update &&
              <Tooltip title="Editer le dossier">
                <Button
                  onClick={editCase(row)}
                  color={"warning"}
                  className={classes.actionButton}
                >
                  <EditIcon fontSize="large" />
                </Button>
              </Tooltip>
            }
            {cee_update &&
              <Tooltip title="Supprimer le dossier'">
                <CustomDeleteButton
                  deleteTitle={"Suppression du dossier"}
                  deleteSubTitle={`Êtes-vous sûr de vouloir supprimer le dossier?`}
                  handleSupprimer={deleteCaseFunction(row)}
                />
              </Tooltip>
            }
          </div>
        </>
      ),
    },
  ];

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleEditClose = () => {
    setOpen(false);
  };

  const handleClickStatusOpen = () => {
    setOpenStatus(true);
  };
  const handleEditStatusClose = () => {
    setOpenStatus(false);
  };


  const editCase = (row) => async () => {
    let obj = dataCases.find((o) => o.id === row.id);
    if (obj) {
      setCurrentCase(obj);
      handleClickOpen();
    }
  }

  const editCaseStatus = (row) => async () => {
    console.log(row);
    console.log(dataCases);
    let obj = dataCases.find((o) => o.id === row.id);
    if (obj) {
      setCurrentCase(obj);
      handleClickStatusOpen();
    }
  }


  const deleteCaseFunction = (row) => async () => {
    let obj = dataCases.find((o) => o.id === row.id);
    let idSelected = obj.id;
    let url = deleteCEECampaignMembershipCase(idSelected);
    axios
      .delete(url, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      })
      .then((res) => {
        if (res.status == 200) {
          setNotification(
            "success",
            `Le dossier a été supprimé`
          );
          refetch();
        } else setNotification("error", "Une erreur est survenue");
      })
      .catch((error) => {
        setNotification("error", "Une erreur est survenue");
      });
  };

  const setNotification = (status, text) => {
    if (status === "success") {
      NotificationManager.success(text);
    } else {
      NotificationManager.error(text);
    }
  };

  const handleSelectedRowsChange = (selection) => {
    setSelectedCases(selection.selectedRows);
  };

  return (
    <>
      <DataTable
          paginationRowsPerPageOptions={[10,20,30,50,100]}
        noDataComponent={<NoData>Aucun dossier à afficher</NoData>}
        noHeader={true}
        columns={columns}
        persistTableHead
        pagination
        subHeader
        progressPending={gridState.loading}
        progressComponent={<LinearProgress />}
        onSelectedRowsChange={handleSelectedRowsChange}
        selectableRows={true}
        pointerOnHover
        onRowClicked={(row) => {
          let obj = dataCases.find((o) => o.id === row.id);
          if (obj) {
            setCurrentCase(obj);
            handleClickOpen();
          }
        }}
        paginationComponentOptions={{
          noRowsPerPage: false,
          rowsPerPageText: "Lignes par page",
          rangeSeparatorText: "sur",
          selectAllRowsItem: false,
          selectAllRowsItemText: "Selectionner toutes les lignes",
        }}
        {...gridState}
      />
      <Dialog
        onClose={handleEditClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="md"
        open={open}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditClose}>
          Modification du dossier
        </CustomDialogTitle>
        <DialogContent dividers>
          <CaseDialog
            currentCase={currentCase}
            closeModal={handleEditClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
      <Dialog
        onClose={handleEditStatusClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={openStatus}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditStatusClose}>
          Modification du dossier
        </CustomDialogTitle>
        <DialogContent dividers>
          <EditCaseStatusDialog
            currentCase={currentCase}
            closeModal={handleEditStatusClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
    </>
  );
};

export default RTCases;
